import { isEmpty } from 'es-toolkit/compat';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Applicant } from '@/components/pages/Applicant/Applicant.types';
import { useApplicantQuery } from '@/components/shared/api/queries/applicant';

import { useLoadingProvider } from '../LoadingContextProvider';
import {
    ApplicantContextProps,
    ProductNameFields,
    ProductNames,
} from './ApplicantContextProvider.types';

const APPLICANT_PAGE_PRODUCTS = [
    ProductNames.FACTORING,
    ProductNames.LOADPAY,
    ProductNames.TCSFUEL,
];

const ApplicantContext = createContext<ApplicantContextProps>({
    applicantProductNames: [],
    hasSalesforceLeadLink: false,
    hasSalesforceOpportunityLink: false,
});

export const ApplicantContextProvider = ({ children }: { children: ReactNode }) => {
    const { applicantId } = useParams();
    const { setIsLoading } = useLoadingProvider();
    const [applicant, setApplicant] = useState<Applicant>();

    const applicantPartnerId = applicant?.faasPartnerId;
    const applicantStatus = applicant?.factoringProduct?.mostRecentDecisionDetail?.status;
    const applicantLoadPayStatus = applicant?.loadPayProduct?.mostRecentDecisionDetail?.status;

    const applicantProductNames = useMemo(() => {
        if (!applicant) return [];

        return APPLICANT_PAGE_PRODUCTS.filter(
            (product) => !isEmpty(applicant[ProductNameFields[product]]),
        );
    }, [applicant]);

    const productName = !isEmpty(applicant?.factoringProduct)
        ? ProductNames.FACTORING
        : ProductNames.LOADPAY;

    const { data: applicantData, isLoading: isApplicantDataLoading } = useApplicantQuery({
        applicantId: applicantId ?? '',
        refetchInterval: 30_000,
    });

    useEffect(() => {
        setIsLoading(isApplicantDataLoading);

        if (applicantData) {
            setApplicant(applicantData);
        }
    }, [applicantData, isApplicantDataLoading]);

    const hasSalesforceLeadLink = Boolean(applicant?.salesforceLeadLink);
    const hasSalesforceOpportunityLink = Boolean(applicant?.salesforceOpportunityLink);

    return (
        <ApplicantContext.Provider
            value={{
                applicant,
                applicantId,
                applicantPartnerId,
                applicantProductNames,
                applicantStatus,
                applicantLoadPayStatus,
                hasSalesforceLeadLink,
                hasSalesforceOpportunityLink,
                productName,
            }}
        >
            {children}
        </ApplicantContext.Provider>
    );
};

export const useApplicantContext = () => useContext(ApplicantContext);
